import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const Layout = () => {
  const location = useLocation();
  const path = location.pathname;

  return (
    <div className="min-h-screen flex flex-col relative bg-texture">
      {/* Navigation */}
      <nav className="w-full py-6 px-8 md:px-16 flex items-center justify-between z-10 relative bg-sand/80 backdrop-blur-sm sticky top-0 border-b border-olive/5">
        <div className="flex flex-col items-center justify-center group cursor-pointer">
          <div className="flex items-center gap-1 text-rust mb-1 group-hover:scale-110 transition-transform">
            <SafeIcon name="Feather" className="w-4 h-4 transform -rotate-45" />
            <SafeIcon name="Sun" className="w-6 h-6" />
            <SafeIcon name="Feather" className="w-4 h-4 transform rotate-45" />
          </div>
          <Link to="/" className="text-4xl font-display leading-none text-olive tracking-wider">LA CASA</Link>
          <span className="text-[10px] tracking-[0.2em] font-semibold text-rust mt-1">- MEXICAN KITCHEN -</span>
        </div>
        
        <div className="hidden md:flex items-center space-x-10">
          <Link to="/" className={`nav-link ${path === '/' ? 'active' : ''}`}>Home</Link>
          <Link to="/menu" className={`nav-link ${path === '/menu' ? 'active' : ''}`}>Menu</Link>
          <Link to="/about" className={`nav-link ${path === '/about' ? 'active' : ''}`}>About</Link>
          <Link to="/contact" className={`nav-link ${path === '/contact' ? 'active' : ''}`}>Contact</Link>
        </div>
        
        <div className="hidden md:block">
          <button className="btn-primary hover:scale-105 transition-transform">RESERVATIONS</button>
        </div>
        
        <button className="md:hidden text-olive">
          <SafeIcon name="Menu" className="w-6 h-6" />
        </button>
      </nav>

      {/* Main Content */}
      <main className="flex-grow flex flex-col">
        <Outlet />
      </main>

      {/* Enhanced Footer */}
      <footer className="bg-olive-dark text-sand pt-24 pb-12 px-8 md:px-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
          {/* Column 1: Brand & Philosophy */}
          <div className="space-y-8">
            <div>
              <div className="flex items-center gap-2 text-rust mb-6">
                <SafeIcon name="Sun" className="w-10 h-10" />
                <span className="font-display text-4xl tracking-wider text-sand">LA CASA</span>
              </div>
              <p className="text-sm opacity-80 leading-relaxed mb-8 italic">
                "A culinary journey to the heart of Mexico, bringing traditional flavors and modern craftsmanship to every plate since 1998."
              </p>
            </div>
            
            <div className="flex space-x-5">
              <a href="#" className="w-12 h-12 flex items-center justify-center border border-sand/20 rounded-full hover:bg-rust hover:border-rust hover:scale-110 transition-all duration-300">
                <SafeIcon name="Instagram" className="w-6 h-6" />
              </a>
              <a href="#" className="w-12 h-12 flex items-center justify-center border border-sand/20 rounded-full hover:bg-rust hover:border-rust hover:scale-110 transition-all duration-300">
                <SafeIcon name="Facebook" className="w-6 h-6" />
              </a>
              <a href="#" className="w-12 h-12 flex items-center justify-center border border-sand/20 rounded-full hover:bg-rust hover:border-rust hover:scale-110 transition-all duration-300">
                <SafeIcon name="Twitter" className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="font-display text-2xl text-rust mb-8 tracking-widest uppercase">Explore</h4>
            <ul className="space-y-5 text-sm font-medium tracking-wide">
              <li><Link to="/" className="hover:text-rust transition-colors flex items-center gap-3"><SafeIcon name="ArrowRight" className="w-4 h-4 text-rust/50" /> Home</Link></li>
              <li><Link to="/menu" className="hover:text-rust transition-colors flex items-center gap-3"><SafeIcon name="ArrowRight" className="w-4 h-4 text-rust/50" /> Our Menu</Link></li>
              <li><Link to="/about" className="hover:text-rust transition-colors flex items-center gap-3"><SafeIcon name="ArrowRight" className="w-4 h-4 text-rust/50" /> Our Story</Link></li>
              <li><Link to="/contact" className="hover:text-rust transition-colors flex items-center gap-3"><SafeIcon name="ArrowRight" className="w-4 h-4 text-rust/50" /> Locations</Link></li>
              <li><button className="hover:text-rust transition-colors flex items-center gap-3 text-left"><SafeIcon name="ArrowRight" className="w-4 h-4 text-rust/50" /> Private Events & Catering</button></li>
              <li><button className="hover:text-rust transition-colors flex items-center gap-3 text-left"><SafeIcon name="ArrowRight" className="w-4 h-4 text-rust/50" /> Gift Cards</button></li>
            </ul>
          </div>

          {/* Column 3: Contact & Location */}
          <div>
            <h4 className="font-display text-2xl text-rust mb-8 tracking-widest uppercase">Visit Us</h4>
            <div className="space-y-8">
              <div className="flex gap-4 group cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-sand/5 flex items-center justify-center shrink-0 group-hover:bg-rust transition-colors">
                  <SafeIcon name="MapPin" className="w-5 h-5 text-rust group-hover:text-sand" />
                </div>
                <p className="text-sm opacity-80 leading-relaxed">
                  <span className="block font-bold text-sand mb-1">DOWNTOWN FLAGSHIP</span>
                  123 Culinary Avenue<br />Food District, FD 90210<br />San Francisco, CA
                </p>
              </div>
              <div className="flex gap-4 group cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-sand/5 flex items-center justify-center shrink-0 group-hover:bg-rust transition-colors">
                  <SafeIcon name="Phone" className="w-5 h-5 text-rust group-hover:text-sand" />
                </div>
                <p className="text-sm opacity-80">
                  <span className="block font-bold text-sand mb-1">CALL US</span>
                  General: (555) 123-4567<br />Events: (555) 987-6543
                </p>
              </div>
              <div className="flex gap-4 group cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-sand/5 flex items-center justify-center shrink-0 group-hover:bg-rust transition-colors">
                  <SafeIcon name="Mail" className="w-5 h-5 text-rust group-hover:text-sand" />
                </div>
                <p className="text-sm opacity-80">
                  <span className="block font-bold text-sand mb-1">EMAIL</span>
                  hola@lacasakitchen.demo
                </p>
              </div>
            </div>
          </div>

          {/* Column 4: Hours & Newsletter */}
          <div>
            <h4 className="font-display text-2xl text-rust mb-8 tracking-widest uppercase">Hours</h4>
            <div className="space-y-4 mb-10">
              <div className="flex justify-between border-b border-sand/10 pb-2">
                <span className="text-sm font-semibold">Mon - Thu</span>
                <span className="text-sm opacity-80">11:00 AM - 10:00 PM</span>
              </div>
              <div className="flex justify-between border-b border-sand/10 pb-2">
                <span className="text-sm font-semibold">Fri - Sat</span>
                <span className="text-sm opacity-80">11:00 AM - 11:00 PM</span>
              </div>
              <div className="flex justify-between border-b border-sand/10 pb-2">
                <span className="text-sm font-semibold">Sunday</span>
                <span className="text-sm opacity-80">12:00 PM - 09:00 PM</span>
              </div>
            </div>
            
            <div className="pt-4">
              <h5 className="text-xs font-bold tracking-widest uppercase text-rust mb-4">Join the Familia</h5>
              <div className="flex">
                <input 
                  type="email" 
                  placeholder="Your Email" 
                  className="bg-sand/5 border border-sand/20 px-4 py-2 text-xs w-full focus:outline-none focus:border-rust transition-colors"
                />
                <button className="bg-rust text-sand px-4 py-2 text-xs font-bold uppercase hover:bg-rust-hover transition-colors">JOIN</button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center pt-10 border-t border-sand/10 text-[10px] uppercase tracking-[0.2em] opacity-40">
          <p>© {new Date().getFullYear()} LA CASA MEXICAN KITCHEN. ALL RIGHTS RESERVED.</p>
          <div className="flex gap-8 mt-6 md:mt-0">
            <button className="hover:text-rust transition-colors underline underline-offset-4">Privacy Policy</button>
            <button className="hover:text-rust transition-colors underline underline-offset-4">Terms of Service</button>
            <button className="hover:text-rust transition-colors underline underline-offset-4">Accessibility</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;